(function(global){
  // Visualizer.Helpers.Form module: input listeners and form parsing for configurator
  function _normalizeDoorOpeningKey(key) {
    if (!key || typeof key !== 'string') return key
    if (key === 'doors-simpleOpeningDirection') return 'doors-openingDirection'
    const m = key.match(/^doors-simpleOpeningDirection-(\d+)$/)
    if (m) return `doors-openingDirection-${m[1]}`
    return key
  }

  function _doorOpeningKeyVariants(key) {
    const out = []
    if (!key) return out
    out.push(key)
    const norm = _normalizeDoorOpeningKey(key)
    if (norm && norm !== key) out.push(norm)
    const m = norm && norm.match(/^doors-openingDirection-(\d+)$/)
    if (m) out.push(`doors-simpleOpeningDirection-${m[1]}`)
    if (norm === 'doors-openingDirection') out.push('doors-simpleOpeningDirection')
    return Array.from(new Set(out))
  }
  function _coerceNumberLoose(raw) {
    if (raw === undefined || raw === null) return undefined
    const s = String(raw).trim().replace(',', '.')
    const n = parseFloat(s)
    return Number.isFinite(n) ? n : undefined
  }

  function _readMinMax(input) {
    if (!input || !input.getAttribute) return { min: undefined, max: undefined }
    return {
      min: _coerceNumberLoose(input.getAttribute('min')),
      max: _coerceNumberLoose(input.getAttribute('max')),
    }
  }

  function _shouldTreatAsInteger(input) {
    try {
      const stepAttr = input && input.getAttribute ? input.getAttribute('step') : null
      if (stepAttr === null || stepAttr === undefined || stepAttr === '') return true // default step=1
      const step = _coerceNumberLoose(stepAttr)
      if (!Number.isFinite(step)) return true
      return Math.abs(step - Math.round(step)) < 1e-9
    } catch (e) {
      return true
    }
  }

  function _clampToRange(n, min, max) {
    let v = n
    if (!Number.isFinite(v)) {
      if (Number.isFinite(min)) v = min
      else if (Number.isFinite(max)) v = max
      else return undefined
    }
    if (Number.isFinite(min) && v < min) v = min
    if (Number.isFinite(max) && v > max) v = max
    return v
  }

  function _normalizeDimensionsInput(inputEl, key, config) {
    try {
      if (!inputEl) return
      if (!key || typeof key !== 'string') return
      if (!key.startsWith('dimensions-')) return

      // Prevent re-entrant loops: this function may dispatch change/input events,
      // and some listeners call back into normalization.
      if (inputEl.__vizNormalizingDimensions) return
      inputEl.__vizNormalizingDimensions = true

      const { min, max } = _readMinMax(inputEl)
      const parsed = _coerceNumberLoose(inputEl.value)
      let clamped = _clampToRange(parsed, min, max)
      if (clamped === undefined) return
      if (_shouldTreatAsInteger(inputEl)) clamped = Math.round(clamped)

      const desired = String(clamped)
      const changed = inputEl.value !== desired
      if (changed) inputEl.value = desired
      if (config && typeof config === 'object') config[key] = desired

      if (changed) {
        try { inputEl.dispatchEvent(new Event('input', { bubbles: true })) } catch (e) {}
        try { inputEl.dispatchEvent(new Event('change', { bubbles: true })) } catch (e) {}
      }
    } catch (e) {
      // best-effort
    } finally {
      try { if (inputEl) inputEl.__vizNormalizingDimensions = false } catch (e) {}
    }
  }

  function toHexColor(input) {
    if (!input || typeof input !== 'string') return input
    const color = input.trim()
    // Handle hex formats
    if (color.startsWith('#')) {
      let hex = color.slice(1)
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('')
      }
      if (hex.length === 6) {
        return '#' + hex.toUpperCase()
      }
      return color
    }
    // Handle rgb/rgba formats
    const match = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/i)
    if (match) {
      const r = Math.max(0, Math.min(255, parseInt(match[1], 10)))
      const g = Math.max(0, Math.min(255, parseInt(match[2], 10)))
      const b = Math.max(0, Math.min(255, parseInt(match[3], 10)))
      const hex = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase()
      return hex
    }
    return color
  }
  // Normalize image src into a stable key (just the filename, lowercase)
  function normalizeImageSrc(src) {
    try {
      const u = new URL(src, window.location.origin)
      const pathname = u.pathname || ''
      const clean = pathname.split('/').pop() || pathname
      return (clean || String(src)).split('?')[0].toLowerCase()
    } catch (e) {
      const noQuery = String(src).split('?')[0]
      return noQuery.substring(noQuery.lastIndexOf('/') + 1).toLowerCase()
    }
  }
  function _isColumnScopedKey(key) {
    if (!key || typeof key !== 'string') return false
    return (
      /^columnTemplate-\d+$/i.test(key) ||
      /^columnShelvesCount-\d+$/i.test(key) ||
      /^columnDrawersCount-\d+$/i.test(key) ||
      /^columnDividerHeight-\d+$/i.test(key) ||
      /^shelvesCount-\d+$/i.test(key) ||
      /^doors-openingDirection-\d+$/i.test(key) ||
      /^doors-simpleOpeningDirection-\d+$/i.test(key)
    )
  }
  function _syncDpoSliderUIFromValue(node, numericValue) {
    try {
      if (!node || !node.classList || !node.classList.contains('dpo-slider-value')) return
      const control = node.closest('.control')
      const layoutFn =
        window &&
        window.__dpoJqSliderLayout &&
        typeof window.__dpoJqSliderLayout.updateDpoJqSliderUI === 'function'
          ? window.__dpoJqSliderLayout.updateDpoJqSliderUI
          : null
      if (!control || !layoutFn) return
      const minAttr = Number(node.getAttribute('min'))
      const maxAttr = Number(node.getAttribute('max'))
      const current = Number(numericValue)
      const min = Number.isFinite(minAttr) ? minAttr : 0
      const max = Number.isFinite(maxAttr) ? maxAttr : (Number.isFinite(min) ? min : 100)
      const value = Number.isFinite(current) ? current : min
      const apply = () => {
        try {
          layoutFn(control, node, min, max, value, { updateJqUi: true, defer: true })
        } catch (e) {}
      }
      apply()
      setTimeout(apply, 0)
      setTimeout(apply, 120)
      setTimeout(apply, 300)
    } catch (e) {}
  }

  function syncScalarInputDomFromConfig(node, configKey, config = {}) {
    if (!node || !configKey || !config || config[configKey] == null) return false
    let cfgVal = config[configKey]
    if (Array.isArray(cfgVal)) cfgVal = cfgVal[0]
    if (cfgVal == null) return false

    const desired = String(cfgVal)
    let nextValue = desired
    const minAttr = Number(node.getAttribute && node.getAttribute('min'))
    const maxAttr = Number(node.getAttribute && node.getAttribute('max'))
    const numericDesired = Number(desired)
    if (Number.isFinite(numericDesired)) {
      const min = Number.isFinite(minAttr) ? minAttr : numericDesired
      const max = Number.isFinite(maxAttr) ? maxAttr : numericDesired
      const clamped = Math.max(min, Math.min(max, numericDesired))
      nextValue = String(clamped)
      config[configKey] = nextValue
    }

    const changed = String(node.value) !== nextValue
    if (changed) {
      node.value = nextValue
      try { node.dispatchEvent(new Event('input', { bubbles: true })) } catch (e) {}
      try { node.dispatchEvent(new Event('change', { bubbles: true })) } catch (e) {}
    }

    if (node.classList && node.classList.contains('dpo-slider-value')) {
      _syncDpoSliderUIFromValue(node, Number(nextValue))
    }
    return changed || (node.classList && node.classList.contains('dpo-slider-value'))
  }

  function _deriveOptionValue(wrap, input) {
    if (!wrap) return input ? input.value : null
    if (wrap.querySelector && wrap.querySelector('.dpo-choice-none')) return 'null'
    const keyValue = Array.from(wrap.classList || []).find(cls => cls.startsWith('data-visualizer-value-'))
    if (keyValue) return keyValue.replace(/^data-visualizer-value-/, '')
    const img = wrap.querySelector && wrap.querySelector('.itoris-dynamicoptions-thumbnail-image')
    if (img && (img.getAttribute('src') || img.src)) return normalizeImageSrc(img.getAttribute('src') || img.src)
    const colorEl = wrap.querySelector && wrap.querySelector('.itoris-dynamicoptions-thumbnail-color')
    if (colorEl) {
      const dataColor = colorEl.getAttribute('data-color') || colorEl.dataset?.color
      const inlineColor = colorEl.style && colorEl.style.backgroundColor
      const computedColor = window.getComputedStyle ? window.getComputedStyle(colorEl).getPropertyValue('background-color') : ''
      const candidates = [dataColor, inlineColor, computedColor].filter(Boolean)
      for (const candidate of candidates) {
        const hex = toHexColor(candidate)
        if (hex && hex !== 'rgba(0, 0, 0, 0)' && hex !== 'transparent') return hex
      }
    }
    return input ? input.value : null
  }

  function _isExplicitlyHiddenForOptionInput(el) {
    let cur = el
    while (cur && cur.nodeType === 1) {
      if (cur.hasAttribute && cur.hasAttribute('hidden')) return true
      const style = cur.style || {}
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return true
      cur = cur.parentElement
    }
    return false
  }

  function _isHiddenForOptionSkip(fieldName, el) {
    if (_isColumnScopedKey(fieldName)) return _isElementHiddenForColumnState(el)
    return _isExplicitlyHiddenForOptionInput(el)
  }

  function _isElementHiddenForColumnState(el) {
    let cur = el
    while (cur && cur.nodeType === 1) {
      if (cur.classList && cur.classList.contains('visualizer-column-interface--hidden')) return true
      if (cur.hasAttribute && cur.hasAttribute('hidden')) return true
      const isSelfHiddenSliderInput =
        cur === el && cur.classList && cur.classList.contains('dpo-slider-value')
      if (!isSelfHiddenSliderInput) {
        const style = cur.style || {}
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return true
        if (window.getComputedStyle) {
          const computed = window.getComputedStyle(cur)
          if (
            computed &&
            (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0')
          ) {
            return true
          }
        }
      }
      cur = cur.parentElement
    }
    return false
  }
  const Form = {
    saveTextInput(node, state, config) {
      const observer = new MutationObserver(() => {
        if (_isColumnScopedKey(state && state.name) && _isElementHiddenForColumnState(node)) return
        state.value = node.value
        config[state.name] = state.value
      })
      node.addEventListener('input', () => {
        if (_isColumnScopedKey(state && state.name) && _isElementHiddenForColumnState(node)) return
        state.value = node.value
        config[state.name] = state.value
      })
      const maybeClamp = () => {
        if (_isColumnScopedKey(state && state.name) && _isElementHiddenForColumnState(node)) return
        _normalizeDimensionsInput(node, state && state.name, config)
        state.value = node.value
        config[state.name] = state.value
      }
      node.addEventListener('blur', maybeClamp)
      node.addEventListener('change', maybeClamp)
      if (node.parentNode) {
        observer.observe(node.parentNode, { childList: true, subtree: true, attributes: true })
      }
    },

    saveRadioInput(state, event, config, opts = {}) {
      const { skipInitialUpdate = false, skipWhenHidden = false } = opts
      function update() {
        if (skipWhenHidden && _isHiddenForOptionSkip(state.name, event.target)) return
        if (event.target.checked === false) return
        const wrap = (event.target && event.target.closest) ? (event.target.closest('.admin__field-option') || event.target.parentNode) : event.target.parentNode
        if (wrap && wrap.querySelector && wrap.querySelector('.dpo-choice-none')) {
          state.value = 'null'
          config[state.name] = state.value
          return
        }
        let keyValue = Array.from((wrap && wrap.classList) ? wrap.classList : []).find(key => key.startsWith('data-visualizer-value-'))
        if (keyValue) {
          state.value = keyValue.replace(/^data-visualizer-value-/, '')
          config[state.name] = state.value
          return
        }
        let valueEl = wrap && wrap.querySelector ? wrap.querySelector('.itoris-dynamicoptions-thumbnail-image') : null
        if (valueEl) {
          state.value = normalizeImageSrc(valueEl.src)
          config[state.name] = state.value
          return
        }
        valueEl = wrap && wrap.querySelector ? wrap.querySelector('.itoris-dynamicoptions-thumbnail-color') : null
        if (valueEl) {
          const bg = window.getComputedStyle(valueEl).getPropertyValue('background-color')
          state.value = toHexColor(bg)
          config[state.name] = state.value
          return
        }
        state.value = event.target.value
        config[state.name] = state.value
      }
      event.target.addEventListener('change', () => { update() })
      if (!skipInitialUpdate) update()
      const observer = new MutationObserver(update)
      if (event.target.parentNode) {
        observer.observe(event.target.parentNode, { childList: true, subtree: true, attributes: true })
      }
    },

    saveSelectInput(state, event, config) {
      if (_isColumnScopedKey(state && state.name) && _isElementHiddenForColumnState(event && event.target)) return
      state.value = event.target.value
      config[state.name] = state.value
    },

    saveMultiSelectInput(state, event, config) {
      if (_isColumnScopedKey(state && state.name) && _isElementHiddenForColumnState(event && event.target)) return
      let result = null
      if (event.target.options) {
        for (let i = 0; i < event.target.options.length; i++) {
          if (event.target.options[i].selected) {
            result ? result.push(event.target.options[i].value) : result = [event.target.options[i].value]
          }
        }
      }
      state.value = result
      config[state.name] = state.value
    },

    saveCheckboxInput(state, event, config, opts = {}) {
      const { skipInitialUpdate = false, skipWhenHidden = false } = opts
      function update() {
        if (skipWhenHidden && _isHiddenForOptionSkip(state.name, event.target)) return
        if (!Array.isArray(state.value)) state.value = state.value ? [state.value] : []
        let result
        const wrap = (event.target && event.target.closest) ? (event.target.closest('.admin__field-option') || event.target.parentNode) : event.target.parentNode
        let keyValue = Array.from((wrap && wrap.classList) ? wrap.classList : []).find(key => key.startsWith('data-visualizer-value-'))
        if (keyValue) {
          result = keyValue.replace(/^data-visualizer-value-/, '')
        }
        let valueEl = wrap && wrap.querySelector ? wrap.querySelector('.itoris-dynamicoptions-thumbnail-image') : null
        if (valueEl) {
          result = normalizeImageSrc(valueEl.src)
        }
        valueEl = wrap && wrap.querySelector ? wrap.querySelector('.itoris-dynamicoptions-thumbnail-color') : null
        if (valueEl) {
          const bg = window.getComputedStyle(valueEl).getPropertyValue('background-color')
          result = toHexColor(bg)
        }
        if (!result) {
          result = event.target.value
        }
        if (event.target.checked) {
          if (!state.value.includes(result)) state.value.push(result)
        } else {
          if (state.value.includes(result)) state.value.splice(state.value.findIndex(item => item === result), 1)
        }
        config[state.name] = state.value
      }
      event.target.addEventListener('change', () => { update() })
      if (!skipInitialUpdate) update()
      const observer = new MutationObserver(update)
      if (event.target.parentNode) {
        observer.observe(event.target.parentNode, { childList: true, subtree: true, attributes: true })
      }
    },

    setSlideListener(input, state, config) {
      const progressNode = input.closest('.control')
      if (!progressNode) return
      const commit = (event) => {
        const isColumnScoped = _isColumnScopedKey(state && state.name)
        if (isColumnScoped && _isElementHiddenForColumnState(input)) return
        if (
          isColumnScoped &&
          config[state.name] != null &&
          String(config[state.name]) !== String(input.value) &&
          !(event && event.isTrusted)
        ) {
          syncScalarInputDomFromConfig(input, state.name, config)
          return
        }
        state.value = input.value
        config[state.name] = state.value
      }
      const observer = new MutationObserver(() => {
        commit(null)
      })
      observer.observe(progressNode, { childList: true, subtree: true })
      input.addEventListener('input', commit)
      input.addEventListener('change', commit)
    },

    // Returns { inputs, addedCount }
    getFormData(form, inputs, config) {
      if (!form) {
        return { inputs, addedCount: 0 }
      }
      const newInputs = Array.from(form.querySelectorAll('[visualizer]'))
        .filter((node) => !inputs.find(item => item.node.id === node.id))
        .map(node => {
          const state = { node, name: _normalizeDoorOpeningKey(node.getAttribute('visualizer')) }
          if (node.classList.contains('input-text')) {
            if (node.hasAttribute('const')) {
              state.value = node.getAttribute('const')
            } else {
              state.value = node.value
              if (node.classList.contains('dpo-slider-value')) Form.setSlideListener(node, state, config)
              else Form.saveTextInput(node, state, config)
            }
          } else if (node.classList.contains('options-list')) {
            const savedValue = config[state.name]
            const hasSaved = savedValue != null
            state.value = hasSaved ? savedValue : null
            if (hasSaved) {
              try {
                Form.reapplyOptionsFromConfig(config, node, {
                  onlyVisible: _isColumnScopedKey(state.name),
                })
              } catch (reapplyErr) {
                console.warn('Failed to reapply config to options list', reapplyErr)
              }
            }
            node.querySelectorAll('input[type=radio]').forEach(input => {
              Form.saveRadioInput(state, { target: input }, config, { skipInitialUpdate: hasSaved, skipWhenHidden: true })
            })
            node.querySelectorAll('input[type=checkbox]').forEach((input) => {
              Form.saveCheckboxInput(state, { target: input }, config, { skipInitialUpdate: hasSaved, skipWhenHidden: true })
            })
          } else if (node.classList.contains('multiselect')) {
            state.value = node.value
            node.addEventListener('change', (event) => {
              Form.saveMultiSelectInput(state, event, config)
            })
          } else if (node.nodeName === 'SELECT') {
            state.value = node.value
            node.addEventListener('change', (event) => {
              Form.saveSelectInput(state, event, config)
            })
          }
          if (!config[state.name])
            config[state.name] = state.value
          return state
        })
      let addedCount = 0
      if (newInputs.length > 0) {
        inputs = [...inputs, ...newInputs]
        addedCount = newInputs.length
      }
      return { inputs, addedCount }
    },

    normalizeDimensionsInputs(root, config) {
      try {
        const scope = root || document
        const nodes = Array.from(scope.querySelectorAll('[visualizer^="dimensions-"]'))
        nodes.forEach((n) => {
          const key = n && n.getAttribute ? n.getAttribute('visualizer') : null
          _normalizeDimensionsInput(n, key, config)
        })
      } catch (e) {
        // best-effort
      }
    },

    reapplyOptionsFromConfig(config = {}, root = document, opts = {}) {
      const scope = root || document
      const { onlyColumnScoped = false, onlyVisible = false } = opts || {}
      const lists = Array.from(scope.querySelectorAll('.options-list'))
      lists.forEach((list) => {
        const rawName = list.getAttribute('visualizer')
        const name = _normalizeDoorOpeningKey(rawName)
        if (!rawName && !name) return
        const scopedName = name || rawName
        if (onlyColumnScoped && !_isColumnScopedKey(scopedName)) return
        if (onlyVisible && _isElementHiddenForColumnState(list)) return
        const cfgVal =
          (name && config[name] != null) ? config[name] :
          (rawName && config[rawName] != null) ? config[rawName] :
          undefined
        if (cfgVal == null) return
        const radios = Array.from(list.querySelectorAll('input[type="radio"]'))
        const checkboxes = Array.from(list.querySelectorAll('input[type="checkbox"]'))

        const findMatch = () => {
          for (const r of radios) {
            const wrap = r.closest('.admin__field-option') || r.parentNode
            if (!wrap) continue
            const val = _deriveOptionValue(wrap, r)
            if (cfgVal === val) return r
          }
          return null
        }
        const match = findMatch()
        if (match && !match.checked) {
          match.checked = true
          match.dispatchEvent(new Event('change', { bubbles: true }))
        }

        if (checkboxes.length > 0) {
          const cfgValues = Array.isArray(cfgVal) ? cfgVal.map(v => String(v)) : [String(cfgVal)]
          checkboxes.forEach((chk) => {
            const wrap = chk.closest('.admin__field-option') || chk.parentNode
            const val = _deriveOptionValue(wrap, chk)
            const shouldCheck = cfgValues.includes(String(val))
            if (shouldCheck !== chk.checked) {
              chk.checked = shouldCheck
              chk.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        }
      })
    },

    reapplyScalarInputsFromConfig(config = {}, root = document, opts = {}) {
      const scope = root || document
      const { onlyColumnScoped = false, onlyVisible = false } = opts || {}
      const nodes = Array.from(scope.querySelectorAll('[visualizer]'))
      nodes.forEach((node) => {
        if (!node || !node.getAttribute) return
        if (node.classList && node.classList.contains('options-list')) return
        const nodeType = (node.type || '').toLowerCase()
        if (nodeType === 'radio' || nodeType === 'checkbox') return
        if (typeof node.value === 'undefined') return

        const rawName = node.getAttribute('visualizer')
        const name = _normalizeDoorOpeningKey(rawName)
        const configKey =
          (name && config[name] != null) ? name :
          (rawName && config[rawName] != null) ? rawName :
          null
        if (!configKey) return
        if (onlyColumnScoped && !_isColumnScopedKey(configKey)) return
        if (onlyVisible && _isElementHiddenForColumnState(node)) return

        syncScalarInputDomFromConfig(node, configKey, config)
      })
    },

    syncScalarInputDomFromConfig,
  }

  global.Visualizer = global.Visualizer || {}
  global.Visualizer.Helpers = global.Visualizer.Helpers || {}
  global.Visualizer.Helpers.Form = Form

  // --- Persist visualizer config into line item properties before Add to Cart ---
  function buildQueryFromCurrentUrl() {
    const Url = global.Visualizer && global.Visualizer.Helpers && global.Visualizer.Helpers.Url
    if (!Url || typeof Url.buildVisualizerQueryFromSearch !== 'function') return ''
    return Url.buildVisualizerQueryFromSearch(location.search)
  }

  function buildPersistedQuery(cfg) {
    const fromUrl = buildQueryFromCurrentUrl()
    return fromUrl || buildQueryFromConfig(cfg)
  }

  function buildQueryFromConfig(cfg) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(cfg || {})) {
      if (v === undefined || v === null || v === '') continue
      if (Array.isArray(v)) {
        const joined = v.filter(x => x !== undefined && x !== null && x !== '').join(',')
        if (joined) params.set(k, joined)
      } else {
        params.set(k, v)
      }
    }
    return params.toString()
  }

  function ensureHiddenProp(form, name, value) {
    if (!form) return
    let input = form.querySelector(`input[name="properties[${name}]"]`)
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = `properties[${name}]`
      form.appendChild(input)
    }
    input.value = value
  }

  function collectConfigSnapshot(root) {
    try {
      const config = {}
      let inputs = []
      const rootHasVisualizerFields = !!(root && root.querySelector && root.querySelector('[visualizer], [visualizer-type], .options-list[visualizer]'))
      const dpoContainer = (root && root.querySelector && root.querySelector('.dpo-container')) || document.querySelector('.dpo-container')
      const productInfo = (root && root.querySelector && root.querySelector('.product__info-wrapper')) || document.querySelector('.product__info-wrapper')
      const scope = rootHasVisualizerFields ? root : (dpoContainer || productInfo || root || document)
      const formEl = scope && scope.closest ? scope.closest('form') || scope.querySelector('form') : (root.querySelector ? root.querySelector('form') : null)
      const container = scope || formEl || document
      const res = Form.getFormData(container, inputs, config)
      inputs = res.inputs
      try { Form.normalizeDimensionsInputs(container, config) } catch (e) {}
      return config
    } catch (e) {
      console.warn('Visualizer: failed to collect config snapshot', e)
      return {}
    }
  }

  // --- Helpers to map visualizer keys to human labels and prune properties ---
  function getFieldLabelByVisualizerKey(root, vizKey) {
    const scope = root || document
    let el = scope.querySelector(`[visualizer="${vizKey}"]`)
    if (!el) {
      const variants = _doorOpeningKeyVariants(vizKey)
      for (const v of variants) {
        if (v === vizKey) continue
        el = scope.querySelector(`[visualizer="${v}"]`)
        if (el) break
      }
    }
    if (!el) return ''
    const field = el.closest('.field') || el.closest('[class*="fieldset"]') || el
    const labelEl = field.querySelector(':scope > label, label')
    let label = labelEl ? (labelEl.textContent || '') : ''
    label = label.replace(/\(.*?\)/g, '').replace(/:\s*$/, '').trim()
    return label
  }

  function attachPersistence() {
    const productForms = Array.from(document.querySelectorAll('form[action^="/cart/add"], form[action*="/cart/add"], form[id^="product-form"], form#product-form'))
    const dpoButtons = Array.from(document.querySelectorAll('button[dpo_add2cart], [dpo_add2cart]'))

    function persistToForm(form) {
      const cfg = collectConfigSnapshot(form || document)

      // Persist canonical params for cart link reconstruction/state restore
      const qs = buildPersistedQuery(cfg)
      try { ensureHiddenProp(form, '_viz_params', qs) } catch (e) {}
      // Variant id (Shopify) for safety
      const idInput = form && form.querySelector('input[name="id"]')
      const variantId = idInput && idInput.value ? idInput.value : ''
      try { ensureHiddenProp(form, '_viz_variant', variantId) } catch (e) {}
    }

    // Native Shopify form submit
    productForms.forEach((form) => {
      form.addEventListener('submit', function onSubmit() {
        try { persistToForm(form) } catch (e) { console.warn('Visualizer: persist on submit failed', e) }
      }, { capture: true })
    })

    // DPO add-to-cart button click (happens before their onclick)
    dpoButtons.forEach((btn) => {
      btn.addEventListener('click', function onClick() {
        try {
          const form = btn.closest('form') || document.querySelector('form[action^="/cart/add"], form[id^="product-form"], form#product-form')
          persistToForm(form || document)
        } catch (e) { console.warn('Visualizer: persist on DPO click failed', e) }
      }, { capture: true })
    })
  }

  // Hook into DPO to prune properties at the moment DPO serializes values
  // Refactored into smaller helpers to improve readability and maintainability
  function getProductForm() {
    return document.querySelector('form[action^="/cart/add"], form[action*="/cart/add"], form[id^="product-form"], form#product-form')
  }

  function getColumnsCount(cfg) {
    let count = parseInt(cfg['columnsCount'] || cfg['ColumnsCount'] || cfg['columns_count'] || 0, 10)
    if (!Number.isFinite(count) || count < 0) count = 0
    return count
  }

  function getIndexedLabels(prefix, maxN, form) {
    const labels = []
    const scope = form || document
    for (let i = 1; i <= maxN; i++) {
      const label = getFieldLabelByVisualizerKey(scope, `${prefix}${i}`)
      if (label) labels[i] = label
    }
    return labels
  }

  function delKeyExactOrRegex(out, baseLabel, i, regexFactory) {
    if (!out) return
    if (baseLabel && Object.prototype.hasOwnProperty.call(out, baseLabel)) delete out[baseLabel]
    const re = regexFactory && regexFactory(i)
    if (re) {
      Object.keys(out).forEach(k => { if (re.test(k)) delete out[k] })
    }
  }

  function pruneDoorAndShelves(out, cfg, form) {
    const count = getColumnsCount(cfg)
    const doorLabels = getIndexedLabels('doors-openingDirection-', 50, form)
    const shelfLabels = getIndexedLabels('shelvesCount-', 50, form)

    for (let i = count + 1; i <= 50; i++) {
      delKeyExactOrRegex(out, doorLabels[i], i, (n) => new RegExp(`^Door\\s*side\\s*${n}(?:\\b|:)`, 'i'))
    }
    for (let i = count + 1; i <= 50; i++) {
      delKeyExactOrRegex(out, shelfLabels[i], i, (n) => new RegExp(`^Shelves\\s*${n}(?:\\b|:)`, 'i'))
    }
  }

  function buildConfigurationLink(cfg, form) {
    const qs = buildPersistedQuery(cfg)
    let variantId = ''
    const idInput = form && form.querySelector && form.querySelector('input[name="id"]')
    if (idInput && idInput.value) variantId = String(idInput.value)
    if (!variantId) {
      const hrefParams = new URLSearchParams(location.search)
      const v = hrefParams.get('variant')
      if (v) variantId = v
    }
    const base = new URL(location.pathname, location.origin)
    if (variantId) base.searchParams.set('variant', variantId)
    if (qs) {
      const extra = new URLSearchParams(qs)
      for (const [k, v] of extra.entries()) {
        if (v !== undefined && v !== null && v !== '') base.searchParams.set(k, v)
      }
    }
    return base.toString()
  }

  function wrapCustomizeCartProperties(orig) {
    return function(values) {
      let out = values || {}
      if (orig) {
        try { out = orig.call(this, out) || out } catch(e) { console.warn('Visualizer: original customizeCartProperties failed', e) }
      }
      try {
        const form = getProductForm()
        const cfg = collectConfigSnapshot(form || document) || {}
        pruneDoorAndShelves(out, cfg, form)
        try {
          out['Configuration link'] = buildConfigurationLink(cfg, form)
        } catch (e) {
          console.warn('Visualizer: failed to add Configuration link property', e)
        }
      } catch(e) {
        console.warn('Visualizer: pruning in customizeCartProperties failed', e)
      }
      return out
    }
  }

  function setupDpoHook() {
    try {
      if (!window.dpoOptions || window.__viz_dpo_hooked) return
      const orig = typeof window.dpoOptions.customizeCartProperties === 'function' ? window.dpoOptions.customizeCartProperties : null
      window.dpoOptions.customizeCartProperties = wrapCustomizeCartProperties(orig)
      window.__viz_dpo_hooked = true
    } catch(e) {
      console.warn('Visualizer: setupDpoHook error', e)
    }
  }

  function initAll(){
    attachPersistence()
    // If DPO already initialized, hook now; otherwise wait for their event
    setupDpoHook()
    try {
      if (window.jQuery && window.jQuery(window) && window.jQuery(window).on) {
        window.jQuery(window).on('dpo_loading_finished', function(){
          // DPO just (re)initialized; set up hook again
          window.__viz_dpo_hooked = false
          setupDpoHook()
        })
      } else {
        // Fallback polling for dpoOptions
        let tries = 0
        const t = setInterval(function(){
          if (window.dpoOptions) { setupDpoHook(); clearInterval(t) }
          if (++tries > 20) clearInterval(t)
        }, 250)
      }
    } catch(e) { console.warn('Visualizer: failed to bind DPO hook', e) }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll)
  } else {
    initAll()
  }

})(window);
